import { useState, useEffect } from 'react';
import { History, Loader2, AlertTriangle, TrendingDown, TrendingUp, Building2 } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { toast } from 'sonner';
import { rfqApi, QuoteVersionHistory } from '@/api/services';
import { getErrorMessage } from '@/api/client';
import { QuoteVersionCard } from './QuoteVersionCard';

interface QuoteVersionHistoryDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  rfqId: string;
  lineId: string;
  carrierId: string;
  lineInfo?: {
    lineNumber: number;
    originCity: string;
    destCity: string;
  };
}

export function QuoteVersionHistoryDialog({
  open,
  onOpenChange,
  rfqId,
  lineId,
  carrierId,
  lineInfo,
}: QuoteVersionHistoryDialogProps) {
  const [loading, setLoading] = useState(true);
  const [history, setHistory] = useState<QuoteVersionHistory | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (open && rfqId && lineId && carrierId) {
      loadHistory();
    }
  }, [open, rfqId, lineId, carrierId]);

  const loadHistory = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await rfqApi.getQuoteVersionHistory(rfqId, lineId, carrierId);
      setHistory(data);
    } catch (err) {
      const message = getErrorMessage(err);
      setError(message);
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount: number, currency = 'USD') => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency,
      minimumFractionDigits: 2,
    }).format(amount);
  };

  const getOverallTrend = () => {
    if (!history || history.versions.length < 2) return null;

    const latestVersion = history.versions[0];
    const firstVersion = history.versions[history.versions.length - 1];

    if (latestVersion.totalRate === null || firstVersion.totalRate === null) {
      // Fall back to base rate comparison
      const change = latestVersion.baseRate - firstVersion.baseRate;
      const changePercent = (change / firstVersion.baseRate) * 100;
      return { change, changePercent, currency: latestVersion.currency };
    }

    const change = latestVersion.totalRate - firstVersion.totalRate;
    const changePercent = (change / firstVersion.totalRate) * 100;
    return { change, changePercent, currency: latestVersion.currency };
  };

  const trend = getOverallTrend();

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[85vh] flex flex-col">
        <DialogHeader className="flex-shrink-0">
          <DialogTitle className="flex items-center gap-2">
            <History className="w-5 h-5" />
            Quote Version History
          </DialogTitle>
        </DialogHeader>

        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          </div>
        ) : error ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <AlertTriangle className="w-12 h-12 text-red-500 mb-4" />
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button variant="outline" onClick={loadHistory}>
              Try Again
            </Button>
          </div>
        ) : history && history.totalVersions === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <History className="w-12 h-12 text-muted-foreground/50 mb-4" />
            <h3 className="text-lg font-medium mb-2">No Quote History</h3>
            <p className="text-muted-foreground">
              This carrier has not submitted any quotes for this lane yet.
            </p>
          </div>
        ) : history ? (
          <>
            {/* Carrier Info Header */}
            <div className="flex-shrink-0 bg-muted/30 rounded-lg p-4 mb-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Building2 className="w-5 h-5 text-primary" />
                  </div>
                  <div>
                    <div className="font-semibold">{history.carrierName}</div>
                    <div className="text-sm text-muted-foreground">{history.carrierCode}</div>
                  </div>
                </div>
                <div className="text-right">
                  <Badge variant="outline" className="mb-1">
                    {history.totalVersions} version{history.totalVersions !== 1 ? 's' : ''}
                  </Badge>
                  {lineInfo && (
                    <div className="text-xs text-muted-foreground">
                      Lane {lineInfo.lineNumber}: {lineInfo.originCity} → {lineInfo.destCity}
                    </div>
                  )}
                </div>
              </div>

              {/* Overall Trend Summary */}
              {trend && history.totalVersions > 1 && (
                <div className="mt-3 pt-3 border-t border-border/50">
                  <div className="flex items-center justify-between">
                    <span className="text-sm text-muted-foreground">Overall rate change:</span>
                    <div className="flex items-center gap-2">
                      {trend.change < 0 ? (
                        <>
                          <TrendingDown className="w-4 h-4 text-emerald-600" />
                          <span className="text-emerald-600 font-medium">
                            {formatCurrency(Math.abs(trend.change), trend.currency)} saved ({Math.abs(trend.changePercent).toFixed(1)}%)
                          </span>
                        </>
                      ) : trend.change > 0 ? (
                        <>
                          <TrendingUp className="w-4 h-4 text-red-600" />
                          <span className="text-red-600 font-medium">
                            {formatCurrency(trend.change, trend.currency)} increase ({trend.changePercent.toFixed(1)}%)
                          </span>
                        </>
                      ) : (
                        <span className="text-muted-foreground">No change</span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* Version Timeline */}
            <ScrollArea className="flex-1 -mx-6 px-6">
              <div className="space-y-4 pb-4">
                {history.versions.map((version, index) => (
                  <div key={version.id} className="relative">
                    {/* Timeline connector */}
                    {index < history.versions.length - 1 && (
                      <div className="absolute left-6 top-full h-4 w-0.5 bg-border" />
                    )}
                    <QuoteVersionCard
                      version={version}
                      isFirst={index === history.versions.length - 1}
                    />
                  </div>
                ))}
              </div>
            </ScrollArea>
          </>
        ) : null}
      </DialogContent>
    </Dialog>
  );
}
